import React, { FC, useEffect, useState } from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from '@reduxjs/toolkit';

import type { ReduxState } from 'app/redux/types';

import { API, ResponseStatus } from 'app/api';
import { NAMESPACE_LIVE } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { Spinner } from 'app/design-system/components/Spinner';
import MatchCenterDeferred from 'app/modules/MatchCenter';
import NotFound from 'app/widgets/Errors/NotFound';

import styles from './MatchCenter.module.scss';

// --

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type MatchCenterLiveProps = {
  matchId: number;
  matchNotFound: boolean;
  path: string;
};

const MatchCenter: FC<MatchCenterLiveProps> = ({
  matchNotFound,
  path,
  matchId,
}) => {
  const [notFound, setNotFound] = useState(false);

  const history = useHistory();

  useEffect(() => {
    if (matchNotFound) {
      (async () => {
        try {
          const response = await API.matches.getMatchStatus(matchId);

          if (response.status === ResponseStatus.SUCCESS && (response.data.status === 'closed')) {
            const resultPath = path.replace(NAMESPACE_LIVE, 'results');
            history.push(resultPath);
          }
          else {
            setNotFound(true);
          }
        }
        catch (error: AnyType) {
          error.log('API.matches.getMatchStatus', error);
          setNotFound(true);
        }
      })();
    }
    else {
      setNotFound(false);
    }
  }, [matchNotFound]);

  if (notFound) {
    return (
      <NotFound className={styles.notFoundContainer} />
    );
  }

  if (matchNotFound && !notFound) {
    return (
      <div className={styles.loaderContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <MatchCenterDeferred matchId={matchId} />
  );
};

const mapStateToProps: MapStateToProps<
  MatchCenterLiveProps,
  AnyType,
  ReduxState
> = (state, {
  $betting: {
    navigation: {
      selectors: {
        currentLocation,
      },
    },

    content: {
      selectors: {
        matches,
      },
    },
  },
}) => {
  const { matchId, path } = currentLocation(state);

  const match = matches(state).get(matchId);

  return {
    matchId,
    path,
    matchNotFound: !match,
  };
};

export default compose<FC>(
  withinBettingContext,
  connect(mapStateToProps),
)(MatchCenter);
